<template>
  <!-- Main Pay Grades Modal (teleported so it appears above nav) -->
  <Teleport to="body">
  <div class="modal fade payroll-setup-modal" ref="mainModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Pay Grades Management</h5>
          <button type="button" class="btn-close" @click="hideMainModal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading pay grades...</p>
          </div>

          <div v-else>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0">Manage Pay Grades</h6>
              <button @click="showAddModal" class="btn btn-sm btn-primary">
                <i class="mdi mdi-plus me-1"></i> Add Pay Grade
              </button>
            </div>

            <div class="table-responsive">
              <table class="table table-sm table-hover table-bordered">
                <thead class="table-light">
                  <tr>
                    <th>Grade Name</th>
                    <th>Min Salary</th>
                    <th>Max Salary</th>
                    <th>Midpoint</th>
                    <th>Description</th>
                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="payGrades.length === 0">
                    <td colspan="6" class="text-center py-3">No pay grades found</td>
                  </tr>
                  <tr v-for="grade in payGrades" :key="grade.id">
                    <td>{{ grade.grade_name }}</td>
                    <td>{{ formatCurrency(grade.min_salary) }}</td>
                    <td>{{ formatCurrency(grade.max_salary) }}</td>
                    <td>{{ formatCurrency(grade.midpoint_salary) }}</td>
                    <td>{{ grade.description || 'N/A' }}</td>
                    <td class="text-center">
                      <button @click="editPayGrade(grade)" class="btn btn-sm btn-info me-1">
                        <i class="mdi mdi-pencil"></i>
                      </button>
                      <button @click="deletePayGrade(grade.id)" class="btn btn-sm btn-danger">
                        <i class="mdi mdi-trash-can"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="hideMainModal">Close</button>
        </div>
      </div>
    </div>
  </div>
  </Teleport>

  <!-- Add/Edit Pay Grade Modal -->
  <Teleport to="body">
  <div class="modal fade payroll-setup-modal" ref="formModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
    <div class="modal-dialog modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEditing ? 'Edit Pay Grade' : 'Add Pay Grade' }}</h5>
          <button type="button" class="btn-close" @click="hideFormModal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="savePayGrade">
            <div class="mb-3">
              <label for="gradeName" class="form-label">Grade Name <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                id="gradeName"
                v-model="gradeForm.grade_name"
                required
                autofocus
              >
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="minSalary" class="form-label">Minimum Salary <span class="text-danger">*</span></label>
                <div class="input-group">
                  <span class="input-group-text">KES</span>
                  <input
                    type="number"
                    class="form-control"
                    id="minSalary"
                    v-model="gradeForm.min_salary"
                    required
                    min="0"
                    step="0.01"
                    @input="validateSalaryRange"
                  >
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <label for="maxSalary" class="form-label">Maximum Salary <span class="text-danger">*</span></label>
                <div class="input-group">
                  <span class="input-group-text">KES</span>
                  <input
                    type="number"
                    class="form-control"
                    id="maxSalary"
                    v-model="gradeForm.max_salary"
                    required
                    min="0"
                    step="0.01"
                    @input="validateSalaryRange"
                  >
                </div>
              </div>
            </div>
            <small v-if="salaryError" class="text-danger d-block mb-2">{{ salaryError }}</small>

            <div class="mb-3">
              <label for="midpointSalary" class="form-label">Midpoint Salary</label>
              <div class="input-group">
                <span class="input-group-text">KES</span>
                <input
                  type="number"
                  class="form-control"
                  id="midpointSalary"
                  v-model="gradeForm.midpoint_salary"
                  min="0"
                  step="0.01"
                >
                <button class="btn btn-outline-secondary" type="button" @click="calculateMidpoint">
                  Calculate
                </button>
              </div>
              <small class="text-muted">If not specified, midpoint will be calculated automatically</small>
            </div>

            <div class="mb-3">
              <label for="structureId" class="form-label">Salary Structure</label>
              <select class="form-select" id="structureId" v-model="gradeForm.salary_structure_id">
                <option value="">None</option>
                <option v-for="structure in salaryStructures" :key="structure.id" :value="structure.id">
                  {{ structure.structure_name }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label for="description" class="form-label">Description</label>
              <textarea
                class="form-control"
                id="description"
                v-model="gradeForm.description"
                rows="2"
              ></textarea>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="hideFormModal">Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            @click="savePayGrade"
            :disabled="!isFormValid || submitLoading"
          >
            <span v-if="submitLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ isEditing ? 'Update' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import { authUrl, execute } from "@/api";
import { Modal } from 'bootstrap';

export default {
  name: 'PayGradesManagement',
  data() {
    return {
      loading: false,
      submitLoading: false,
      payGrades: [],
      salaryStructures: [],
      isEditing: false,
      salaryError: '',
      currentId: null,
      gradeForm: this.getEmptyForm(),
      mainModal: null,
      formModal: null
    };
  },
  computed: {
    isFormValid() {
      return this.gradeForm.grade_name &&
        this.gradeForm.min_salary &&
        this.gradeForm.max_salary &&
        parseFloat(this.gradeForm.min_salary) >= 0 &&
        parseFloat(this.gradeForm.max_salary) > 0 &&
        parseFloat(this.gradeForm.max_salary) > parseFloat(this.gradeForm.min_salary) &&
        !this.salaryError;
    }
  },
  mounted() {
    this.initModals();
  },
  beforeUnmount() {
    this.cleanupModals();
  },
  methods: {
    getEmptyForm() {
      return {
        grade_name: '',
        min_salary: '',
        max_salary: '',
        midpoint_salary: '',
        salary_structure_id: '',
        description: ''
      };
    },

    initModals() {
      this.$nextTick(() => {
        // Initialize main modal
        if (this.$refs.mainModal) {
          this.mainModal = new Modal(this.$refs.mainModal, {
            backdrop: 'static',
            keyboard: false
          });
          
          this.$refs.mainModal.addEventListener('hidden.bs.modal', () => {
            this.cleanupBackdrop();
          });
        }

        // Initialize form modal
        if (this.$refs.formModal) {
          this.formModal = new Modal(this.$refs.formModal, {
            backdrop: 'static',
            keyboard: false
          });
          
          this.$refs.formModal.addEventListener('hidden.bs.modal', () => {
            this.resetForm();
          });
        }
      });
    },

    cleanupModals() {
      if (this.mainModal) {
        this.mainModal.dispose();
        this.mainModal = null;
      }
      if (this.formModal) {
        this.formModal.dispose();
        this.formModal = null;
      }
      this.cleanupBackdrop();
    },

    cleanupBackdrop() {
      // Remove any lingering backdrops
      document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    },

    showModal() {
      this.loadPayGrades();
      this.loadSalaryStructures();

      if (this.mainModal) {
        this.$nextTick(() => this.mainModal.show());
      } else {
        this.initModals();
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.mainModal) {
              this.mainModal.show();
            }
          }, 100);
        });
      }
    },

    hideMainModal() {
      this.mainModal?.hide();
    },

    showAddModal() {
      this.isEditing = false;
      this.currentId = null;
      this.resetForm();
      
      // Hide main modal first, then show form modal
      this.mainModal?.hide();
      
      setTimeout(() => {
        this.formModal?.show();
      }, 150); // Wait for main modal to fully hide
    },

    showFormModal() {
      this.formModal?.show();
    },

    hideFormModal() {
      this.formModal?.hide();
      
      // Show main modal after form modal is hidden
      setTimeout(() => {
        this.mainModal?.show();
      }, 150);
    },

    resetForm() {
      this.gradeForm = this.getEmptyForm();
      this.salaryError = '';
      this.submitLoading = false;
      this.isEditing = false;
      this.currentId = null;
    },

    async loadPayGrades() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getPayGrades");

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.payGrades = res.data.data.payGrades || [];
        } else {
          console.error("Error fetching pay grades:", res.data.message);
          alert(res.data.message || "Failed to load pay grades");
        }
      } catch (error) {
        console.error("API Error:", error);
        alert("An error occurred while loading pay grades");
      } finally {
        this.loading = false;
      }
    },

    async loadSalaryStructures() {
      const data = new FormData();
      data.append("function", "getSalaryStructures");

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.salaryStructures = res.data.data.salaryStructures || [];
        }
      } catch (error) {
        console.error("API Error:", error);
      }
    },

    editPayGrade(grade) {
      this.isEditing = true;
      this.currentId = grade.id;
      this.gradeForm = {
        grade_name: grade.grade_name,
        min_salary: grade.min_salary,
        max_salary: grade.max_salary,
        midpoint_salary: grade.midpoint_salary || '',
        salary_structure_id: grade.salary_structure_id || '',
        description: grade.description || ''
      };

      this.mainModal?.hide();
      setTimeout(() => {
        this.formModal?.show();
      }, 150);
    },

    async deletePayGrade(id) {
      if (!confirm("Are you sure you want to delete this pay grade? This action cannot be undone.")) {
        return;
      }

      const data = new FormData();
      data.append("function", "deletePayGrade");
      data.append("id", id);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          alert(res.data.message || "Pay grade deleted successfully");
          await this.loadPayGrades();
        } else {
          alert(res.data.message || "Failed to delete pay grade");
        }
      } catch (error) {
        console.error("API Error:", error);
        alert("An error occurred while deleting pay grade");
      }
    },

    calculateMidpoint() {
      if (this.gradeForm.min_salary && this.gradeForm.max_salary) {
        const min = parseFloat(this.gradeForm.min_salary);
        const max = parseFloat(this.gradeForm.max_salary);
        
        if (min >= 0 && max > 0 && max > min) {
          this.gradeForm.midpoint_salary = ((min + max) / 2).toFixed(2);
          this.salaryError = '';
        } else {
          this.salaryError = 'Maximum salary must be greater than minimum salary';
        }
      } else {
        alert("Please enter both minimum and maximum salary values first");
      }
    },

    validateSalaryRange() {
      if (this.gradeForm.min_salary && this.gradeForm.max_salary) {
        const min = parseFloat(this.gradeForm.min_salary);
        const max = parseFloat(this.gradeForm.max_salary);
        
        if (min >= max) {
          this.salaryError = 'Maximum salary must be greater than minimum salary';
          return false;
        } else {
          this.salaryError = '';
          return true;
        }
      }
      return true;
    },

    async savePayGrade() {
      if (!this.isFormValid) {
        if (!this.validateSalaryRange()) {
          return;
        }
        alert("Please fill all required fields correctly");
        return;
      }

      this.submitLoading = true;
      const data = new FormData();
      data.append("function", this.isEditing ? "updatePayGrade" : "createPayGrade");

      if (this.isEditing) {
        data.append("id", this.currentId);
      }

      data.append("grade_name", this.gradeForm.grade_name);
      data.append("min_salary", this.gradeForm.min_salary);
      data.append("max_salary", this.gradeForm.max_salary);

      // Handle midpoint salary
      if (this.gradeForm.midpoint_salary) {
        data.append("midpoint_salary", this.gradeForm.midpoint_salary);
      } else {
        const min = parseFloat(this.gradeForm.min_salary);
        const max = parseFloat(this.gradeForm.max_salary);
        const midpoint = ((min + max) / 2).toFixed(2);
        data.append("midpoint_salary", midpoint);
      }

      if (this.gradeForm.salary_structure_id) {
        data.append("salary_structure_id", this.gradeForm.salary_structure_id);
      }

      if (this.gradeForm.description) {
        data.append("description", this.gradeForm.description);
      }

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          alert(res.data.message || (this.isEditing ? "Pay grade updated successfully" : "Pay grade created successfully"));
          this.hideFormModal();
          await this.loadPayGrades();
        } else {
          alert(res.data.message || "Failed to save pay grade");
        }
      } catch (error) {
        console.error("API Error:", error);
        alert("An error occurred while saving pay grade");
      } finally {
        this.submitLoading = false;
      }
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value || 0);
    }
  },
  watch: {
    'gradeForm.min_salary'() {
      this.validateSalaryRange();
    },
    'gradeForm.max_salary'() {
      this.validateSalaryRange();
    }
  }
};
</script>

<style scoped>
.modal-dialog-scrollable {
  max-height: calc(100vh - 100px);
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
}

.btn-info {
  color: white;
}

.btn-info:hover {
  color: white;
}
</style>